// Rekap Presensi bulanan: simbol & warna tiap status, rentang bulan, dan penyusunan data dari GET /absensi/kehadiran
// (server menghitung status tiap hari dari jadwal shift, absen, izin/sakit/cuti disetujui, dan hari libur: utils/statusKehadiran.js).

// belum_absen & tanpa_jadwal sengaja tidak punya simbol (sel dikosongkan agar rekap tidak ramai).
export const TAMPIL_STATUS = {
  hadir: { simbol: '✓', warna: '#2e7d32', label: 'Hadir' },
  setengah: { simbol: '½', warna: '#ef6c00', label: 'Setengah hari' },
  izin: { simbol: 'I', warna: '#0277bd', label: 'Izin' },
  sakit: { simbol: 'S', warna: '#c2185b', label: 'Sakit' },
  cuti: { simbol: 'C', warna: '#00838f', label: 'Cuti' },
  alfa: { simbol: 'A', warna: '#d32f2f', label: 'Alfa' },
  libur_nasional: { simbol: 'N', warna: '#5e35b1', label: 'Libur nasional' },
  libur: { simbol: 'L', warna: '#757575', label: 'Libur' },
  belum_pulang: { simbol: '!', warna: '#e65100', label: 'Belum absen pulang' },
  berlangsung: { simbol: '…', warna: '#1565c0', label: 'Sedang bekerja' },
};

/** Kolom jumlah di kanan tabel: [kunci rekap server, judul kolom, keterangan]. */
export const KOLOM_TOTAL = [
  ['hadir', 'H', 'Hadir'], ['setengah', '½', 'Setengah hari'], ['izin', 'I', 'Izin'], ['sakit', 'S', 'Sakit'],
  ['cuti', 'C', 'Cuti'], ['alfa', 'A', 'Alfa'], ['telat', 'T', 'Terlambat (hari masuk yang terlambat)'],
];

const dua = (n) => String(n).padStart(2, '0');

/** Rentang tanggal satu bulan penuh: { dari: 'YYYY-MM-01', sampai: 'YYYY-MM-<akhir>', jumlahHari }. */
export const rentangBulan = (tahun, bulan) => {
  const jumlahHari = new Date(tahun, bulan, 0).getDate();
  return { dari: `${tahun}-${dua(bulan)}-01`, sampai: `${tahun}-${dua(bulan)}-${dua(jumlahHari)}`, jumlahHari };
};

/** Teks tooltip satu sel: "Hadir · terlambat 25 menit · pulang cepat 30 menit". */
export const teksSel = (h) => {
  if (!h) return '';
  const t = TAMPIL_STATUS[h.status];
  if (!t) return '';
  const bagian = [t.label];
  if (h.telatMenit > 0) bagian.push(`terlambat ${h.telatMenit} menit`);
  if (h.pulangCepatMenit > 0) bagian.push(`pulang cepat ${h.pulangCepatMenit} menit`);
  return bagian.join(' · ');
};

/**
 * Susun baris rekap dari hasil GET /absensi/kehadiran tiap cabang.
 * hasil: [{ namaCabang, data: [{ user: { uuid, nama, username }, hari: [{ tanggal, status, telatMenit, pulangCepatMenit }], rekap }] }]
 * Keluaran: [{ userId, username, nama, cabang, hari: { [tanggal ke-]: item }, total: { hadir, setengah, izin, sakit, cuti, alfa, telat } }]
 */
export const bentukRekap = (hasil) => {
  const baris = [];
  for (const c of hasil || []) {
    for (const k of c.data || []) {
      const hari = {};
      for (const h of k.hari || []) hari[Number(String(h.tanggal).slice(8, 10))] = h;
      const total = {};
      for (const [kunci] of KOLOM_TOTAL) total[kunci] = Number((k.rekap || {})[kunci]) || 0;
      baris.push({ userId: k.user.uuid, username: k.user.username, nama: k.user.nama || k.user.username, cabang: c.namaCabang || '', hari, total });
    }
  }
  return baris;
};

/**
 * Ringkasan satu hari dari baris rekap (bentukRekap): jumlah karyawan per kelompok status.
 * hadir = Hadir + Sedang bekerja + Belum absen pulang (terlambat dihitung terpisah dari yang hadir); libur = Libur + Libur nasional.
 */
export const hitungStatusHari = (baris, tanggalIso) => {
  const hari = Number(String(tanggalIso).slice(8, 10));
  const r = { jumlah: (baris || []).length, hadir: 0, terlambat: 0, setengah: 0, izinSakitCuti: 0, alfa: 0, belumAbsen: 0, libur: 0 };
  for (const b of baris || []) {
    const h = b.hari[hari];
    if (!h) continue;
    if (['hadir', 'berlangsung', 'belum_pulang'].includes(h.status)) { r.hadir += 1; if (h.telatMenit > 0) r.terlambat += 1; }
    else if (h.status === 'setengah') r.setengah += 1;
    else if (['izin', 'sakit', 'cuti'].includes(h.status)) r.izinSakitCuti += 1;
    else if (h.status === 'alfa') r.alfa += 1;
    else if (h.status === 'belum_absen') r.belumAbsen += 1;
    else if (['libur', 'libur_nasional'].includes(h.status)) r.libur += 1;
  }
  return r;
};

/**
 * Ambil status kehadiran semua karyawan (GET /absensi/kehadiran) untuk rentang tanggal, satu permintaan per cabang:
 * admin hanya cabangnya sendiri (server mengabaikan ?cabang=), superadmin semua cabang. Hasil: bentukRekap(...).
 * axios dan base (http://host:port) diberikan pemanggil supaya fungsi ini mudah diuji.
 */
export const ambilKehadiranCabang = async (axios, base, dari, sampai) => {
  const daftar = await axios.get(`${base}/cabangbyrole?bentuk=daftar`, { withCredentials: true }).then((r) => {
    const d = r.data?.data ?? r.data;
    return Array.isArray(d) ? d : (d && typeof d === 'object' ? [d] : []);
  });
  const hasil = await Promise.all(daftar.map(async (c) => {
    const r = await axios.get(`${base}/absensi/kehadiran`, { params: { cabang: c.uuid, dari, sampai }, withCredentials: true });
    return { namaCabang: c.namacabang, data: r.data?.data || [] };
  }));
  return bentukRekap(hasil);
};

const jam5 = (t) => String(t || '').slice(0, 5);

/** "Pagi 07:00–15:00" dari objek shift { nama, jamMulai, jamSelesai }; kosong bila tidak ada. */
export const teksShift = (shift) => (shift ? `${shift.nama} ${jam5(shift.jamMulai)}–${jam5(shift.jamSelesai)}` : '');

/** Teks kolom Shift di tabel absensi dari statusKehadiran satu baris. */
export const teksShiftBaris = (sk) => {
  if (!sk) return '-';
  if (sk.shift) return teksShift(sk.shift);
  return sk.jadwalLibur ? 'Libur' : 'Belum ada jadwal';
};

/**
 * Info untuk formulir Presensi Manual dari status satu hari (satu elemen `hari` dari GET /absensi/kehadiran).
 * Mengembalikan { tingkat: 'info' | 'warning', baris: [teks] } atau null. Hanya informasi: absen manual tetap boleh disimpan.
 */
export const infoHariManual = (h) => {
  if (!h) return null;
  const baris = [];
  if (h.shift) baris.push(`Jadwal: ${teksShift(h.shift)}`);
  else baris.push(h.jadwalLibur ? 'Jadwal: libur' : 'Jadwal: belum diatur');
  let tingkat = 'info';
  const t = TAMPIL_STATUS[h.status];
  const nama = h.libur && h.libur.nama ? ` (${h.libur.nama})` : '';
  if (h.status === 'libur_nasional') { tingkat = 'warning'; baris.push(`Libur nasional${nama}. Karyawan tidak dijadwalkan masuk.`); }
  else if (h.status === 'libur') { tingkat = 'warning'; baris.push(`Hari libur${nama}.`); }
  else if (['izin', 'sakit', 'cuti'].includes(h.status)) { tingkat = 'warning'; baris.push(`${t.label} yang disetujui sudah tercatat untuk hari ini.`); }
  else if (['hadir', 'setengah', 'belum_pulang', 'berlangsung', 'alfa'].includes(h.status) && h.masuk) { tingkat = 'warning'; baris.push(`Sudah ada absen pada tanggal ini (${t ? t.label : h.status}, masuk ${jam5(h.masuk)}${h.keluar ? `, pulang ${jam5(h.keluar)}` : ''}).`); }
  return { tingkat, baris };
};

/**
 * Jumlah pengajuan izin/sakit/cuti yang menunggu persetujuan (GET /absensi/pengajuan?status=menunggu), dijumlah semua cabang
 * yang boleh dilihat (admin: cabangnya, superadmin: semua).
 */
export const ambilPengajuanMenunggu = async (axios, base) => {
  const daftar = await axios.get(`${base}/cabangbyrole?bentuk=daftar`, { withCredentials: true }).then((r) => {
    const d = r.data?.data ?? r.data;
    return Array.isArray(d) ? d : (d && typeof d === 'object' ? [d] : []);
  });
  const jumlah = await Promise.all(daftar.map((c) => axios.get(`${base}/absensi/pengajuan`, { params: { cabang: c.uuid, status: 'menunggu' }, withCredentials: true })
    .then((r) => Number(r.data?.menunggu) || 0)));
  return jumlah.reduce((a, n) => a + n, 0);
};

/** Ambang lembur (menit kerja) untuk hari tanpa jadwal shift: lebih dari 8,5 jam sejak masuk. */
export const AMBANG_LEMBUR_TANPA_SHIFT = 510;

const menitJam = (t) => { const [j, m] = String(t).split(':').map(Number); return j * 60 + (m || 0); };

/**
 * Jam kerja & lembur satu hari dari jam masuk/pulang. Lembur dihitung dari jam selesai shift hari itu
 * (shift { jamMulai, jamSelesai }, boleh lewat tengah malam); tanpa shift dipakai ambang AMBANG_LEMBUR_TANPA_SHIFT.
 * Keluaran: { totalHours, totalMinutes, overtime: { hours, minutes } } (totalMinutes = sisa menit di luar jam penuh).
 */
export const hitungJamKerja = (jamMasuk, jamKeluar, shift) => {
  if (!jamMasuk || !jamKeluar) return { totalHours: 0, totalMinutes: 0, overtime: { hours: 0, minutes: 0 } };
  const masuk = menitJam(jamMasuk);
  let keluar = menitJam(jamKeluar);
  if (keluar < masuk) keluar += 24 * 60;
  const total = keluar - masuk;
  let lembur;
  if (shift && shift.jamMulai && shift.jamSelesai) {
    const mulai = menitJam(shift.jamMulai);
    let selesai = menitJam(shift.jamSelesai);
    if (selesai <= mulai) {
      selesai += 24 * 60;
      if (masuk < selesai - 24 * 60) selesai -= 24 * 60; // masuk sesudah tengah malam: bagian akhir shift malam
    }
    lembur = Math.max(0, keluar - selesai);
  } else {
    lembur = Math.max(0, total - AMBANG_LEMBUR_TANPA_SHIFT);
  }
  return { totalHours: Math.floor(total / 60), totalMinutes: total % 60, overtime: { hours: Math.floor(lembur / 60), minutes: lembur % 60 } };
};
